import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Stack,
  useDisclosure,
  Box,
  Collapse,
  Badge,
  HStack,
  VStack,
  Icon,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TimeIcon,
  SearchIcon,
} from "@chakra-ui/icons";

// ─── Shared constants ────────────────────────────────────────
const ACCENT_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const changelogData = {
  "April 2026": [
    "2026-04-17 🔐 Secured admin panel: replaced hardcoded password with server-side PHP sessions + token-based auth across all 20 endpoints.",
    "2026-04-17 📊 Added Sector Allocation donut chart on home page — shows portfolio breakdown by industry sector.",
  ],
  "March 2026": [
    "2026-03-29 🤖 AI-powered Daily Portfolio Insight & Weekly Milestone Coach via Gemini API.",
    "2026-03-28 🔒📊📱 Secured stock API, added Sector/Yield/Weight columns & mobile card view for stocks.",
    "2026-03-21 📱 Improved responsive design across all components (KPI cards, wallet cards, charts, milestones, footer).",
  ],
  "February 2026": [
    "2026-02-21 💰 Dividend tracking system (payments, tax, history)",
  ],
  "January 2026": [
    "2026-01-31 🛡️ Admin panel with login, dashboard & CRUD for Wallets/Transactions/Stocks.",
    "2026-01-30 📐 DividendKPI responsive grid with equal card heights.",
    "2026-01-30 📱 Transactions mobile-responsive card layout.",
    "2026-01-30 💵 Added dollar amount display to 24H change card.",
    "2026-01-30 ⚡ Optimized WalletCard with useMemo & shimmer loading effect.",
    "2026-01-24 🎨 Optimized wallet card: removed QR, added mobile responsive design, improved compact layout.",
  ],
  "November 2025": [
    "2025-11-16 🎨 Improved styling for a better user experience.",
    "2025-11-01 📥 Added wallet crypto DASH, POLYGON.",
  ],
  "October 2025": [
    "2025-10-18 🎨 Fresh visual update",
    "2025-10-17 🌈 Fresh visual update",
    "2025-10-03 💹 Stock table updated: 'Commission' column now records all fees.",
  ],
  "September 2025": [
    "2025-09-16 ✨ Additional milestones are now part of your journey!",
  ],
  "June 2025": [
    "2025-06-24 💰 Added 'Total Paid Dividends' KPI + visual badge for yearly income",
    "2025-06-22 ✅ Added total stock income/year & total dividends received",
  ],
  "May 2025": [
    "2025-05-17 📈 UI improvements: optimized wallet cards for mobile, enhanced transaction and stock tables with badges, color highlights, and safe value formatting.",
    "2025-05-17 📌 Clickable Dividends & Milestones cards added for easier navigation!",
    "2025-05-10 👛 Enhanced Wallet Card: added animated balance reveal and improved clipboard feedback.",
    "2025-05-10 📊 Enhanced Crypto KPI: added value change animation and improved data handling.",
    "2025-05-10 📥 Enhanced Transactions page UX.",
    "2025-05-10 📝 Updated About page layout and content.",
    "2025-05-06 📊 Added a global progress tracker with motivational messages based on overall milestone completion",
  ],
  "April 2025": [
    "2025-04-23 🛠️ UI updates: dark pages, improved chart & badges",
    "2025-04-18 🛠️ Improved changelog modal: arrow toggle, scrollable view, and highlighted dates.",
    "2025-04-18 ✅ Added a new 'Recover %' column showing how much of the initial investment has been recovered through dividends.",
    "2025-04-18 ✅ Added visual bar chart in the 'Profit %' column to better illustrate performance at a glance.",
    "2025-04-15 ✅ Fixed: Profit calculation formula in the Dividends table now displays accurate values.",
    "2025-04-11 ☕ Added support message in footer – Donations go to Mattias' future fund 💰👶",
    "2025-04-05 ✨ Improved alignment and styling table, making it more responsive and user-friendly 📊",
    "2025-04-04 ➕ Added current monthly dividend display ($/mo) at the top of the Milestones page to show real-time progress",
    "2025-04-02 📌 Added 'My Toolkit' page – A new page showcasing the tools and platforms I use for investing",
    "2025-04-01 🔧 Minor Fixes & Improvements",
  ],
  "March 2025": [
    "2025-03-31 📈 New Growth Tracker page – Visualize height 📏 and weight ⚖️ progression with an interactive chart.",
    "2025-03-28 ✅ Fixed: Corrected the calculation and display of Total Portfolio Value and 24h Change.",
    "2025-03-26 📝 Added: Display for Total Portfolio Value and 24h Change in the Crypto section.",
    "2025-03-24 ➕ Added: 'Show More' / 'Show Less' toggle for project details to improve readability and user experience.",
    "2025-03-18 🆕 Added Stocks & Dividends page! 📈 View all stocks held by Mattias in a detailed table.",
    "2025-03-16 🖌️ Minor CSS improvements for better styling and responsiveness.",
    "2025-03-15 👶 Mattias age tracker | Compact design + emojis",
    "2025-03-14 📥 Added Latest 5 Crypto Transactions section with improved UI & responsiveness.",
    "2025-03-13 🗨️ Added live chat support with Tawk.to for real-time user assistance! 🚀",
    "2025-03-11 🎨 Improved wallet card styling for a better user experience.",
    "2025-03-09 🎨 Improved CSS styling, including media queries for better responsiveness.",
    "2025-03-01 💰 Added USD value for each cryptocurrency in the portfolio.",
  ],
  "February 2025": [
    "2025-02-19 ⚙️ Fixed footer alignment issue",
    "2025-02-16 🚀 Project launched!",
  ],
};

// ─── Category detection ──────────────────────────────────────
const getCategory = (entry) => {
  if (entry.includes("🎨") || entry.includes("🌈") || entry.includes("🖌️"))
    return "design";
  if (entry.includes("✅") || entry.includes("🔧") || entry.includes("⚙️"))
    return "fix";
  if (entry.includes("➕") || entry.includes("🆕") || entry.includes("📌"))
    return "feature";
  if (entry.includes("📈") || entry.includes("💹") || entry.includes("📊"))
    return "improvement";
  return "other";
};

const CATEGORY_COLORS = {
  design: { bg: "rgba(159, 122, 234, 0.15)", border: "#9F7AEA", text: "#D6BCFA" },
  fix: { bg: "rgba(72, 187, 120, 0.15)", border: "#48BB78", text: "#9AE6B4" },
  feature: { bg: "rgba(99, 179, 237, 0.15)", border: "#63B3ED", text: "#90CDF4" },
  improvement: { bg: "rgba(56, 178, 172, 0.15)", border: "#38B2AC", text: "#81E6D9" },
  other: { bg: "rgba(160, 174, 192, 0.15)", border: "#A0AEC0", text: "#CBD5E0" },
};

// ─── Changelog Entry ─────────────────────────────────────────
const ChangelogEntry = ({ entry, index }) => {
  const date = entry.slice(0, 10);
  const text = entry.slice(11);
  const category = getCategory(entry);
  const colors = CATEGORY_COLORS[category];

  return (
    <Box
      p={{ base: 2.5, md: 4 }}
      bg={colors.bg}
      borderRadius={{ base: "lg", md: "xl" }}
      borderLeft="4px solid"
      borderColor={colors.border}
      backdropFilter="blur(10px)"
      _hover={{
        bg: colors.bg.replace("0.15", "0.25"),
        transform: "translateX(8px)",
        boxShadow: `0 8px 20px ${colors.border}40`,
      }}
      transition="all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
      cursor="pointer"
      sx={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(20px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <HStack spacing={{ base: 2, md: 3 }} align="start">
        <Badge
          bg={colors.border}
          color="white"
          fontSize={{ base: "2xs", md: "xs" }}
          px={{ base: 1.5, md: 3 }}
          py={{ base: 0.5, md: 1 }}
          borderRadius="lg"
          flexShrink={0}
          fontWeight="semibold"
          boxShadow="sm"
        >
          {date}
        </Badge>
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          color="whiteAlpha.900"
          lineHeight="tall"
          fontWeight="500"
        >
          {text}
        </Text>
      </HStack>
    </Box>
  );
};

// ─── Month Accordion Button ──────────────────────────────────
const MonthButton = ({ month, count, isOpen, onToggle }) => (
  <Button
    size={{ base: "md", md: "lg" }}
    variant="unstyled"
    width="100%"
    onClick={onToggle}
    h="auto"
    py={{ base: 3, md: 4 }}
    px={{ base: 3, md: 5 }}
    bg={isOpen ? "whiteAlpha.100" : "whiteAlpha.50"}
    borderRadius="xl"
    border="1px solid"
    borderColor={isOpen ? "whiteAlpha.300" : "whiteAlpha.100"}
    _hover={{
      bg: "whiteAlpha.150",
      borderColor: "whiteAlpha.300",
      transform: "translateX(4px)",
    }}
    transition="all 0.3s ease"
  >
    <Flex justify="space-between" align="center" w="100%">
      <HStack spacing={{ base: 2, md: 3 }}>
        <Icon
          as={isOpen ? ChevronDownIcon : ChevronRightIcon}
          boxSize={{ base: 4, md: 5 }}
          color="whiteAlpha.700"
          transition="all 0.3s ease"
        />
        <Text
          fontWeight="bold"
          fontSize={{ base: "sm", md: "lg" }}
          color="white"
        >
          {month}
        </Text>
        <Badge
          bg={ACCENT_GRADIENT}
          color="white"
          fontSize={{ base: "2xs", md: "xs" }}
          px={{ base: 2, md: 3 }}
          py={{ base: 0.5, md: 1 }}
          borderRadius="full"
          fontWeight="bold"
        >
          {count}
        </Badge>
      </HStack>
    </Flex>
  </Button>
);

// ─── Main Component ──────────────────────────────────────────
const ModalLog = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const defaultMonth = Object.keys(changelogData)[0];
  const [openMonth, setOpenMonth] = useState(defaultMonth);
  const [searchTerm, setSearchTerm] = useState("");

  const totalEntries = Object.values(changelogData).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  const filteredData = Object.entries(changelogData).reduce(
    (acc, [month, entries]) => {
      const filtered = entries.filter((entry) =>
        entry.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filtered.length > 0) acc[month] = filtered;
      return acc;
    },
    {}
  );

  return (
    <>
      <Button
        onClick={onOpen}
        size="sm"
        position="relative"
        overflow="hidden"
        bg={ACCENT_GRADIENT}
        color="white"
        leftIcon={<TimeIcon />}
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
        }}
        _active={{ transform: "translateY(0)" }}
        transition="all 0.3s ease"
        fontWeight="600"
        px={6}
        borderRadius="xl"
      >
        Changelog
      </Button>

      <Modal
        blockScrollOnMount
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "full", sm: "2xl" }}
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.800"
          backdropFilter="blur(10px)"
          zIndex={1100}
          sx={{ animation: "fadeIn 0.3s ease-out" }}
        />
        <ModalContent
          bg="transparent"
          maxW={{ base: "100%", sm: "900px" }}
          mx={{ base: 0, sm: 4 }}
          my={{ base: 0, sm: "auto" }}
          zIndex={1100}
          sx={{
            animation:
              "slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(40px) scale(0.95)",
              },
              to: { opacity: 1, transform: "translateY(0) scale(1)" },
            },
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box
            bg="linear-gradient(135deg, rgba(26, 32, 44, 0.98) 0%, rgba(45, 55, 72, 0.98) 100%)"
            borderRadius={{ base: "none", sm: "2xl" }}
            overflow="hidden"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5)"
            border={{ base: "none", sm: "1px solid" }}
            borderColor="whiteAlpha.200"
            position="relative"
            minH={{ base: "100dvh", sm: "auto" }}
            display="flex"
            flexDirection="column"
          >
            {/* Animated gradient top bar */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bgGradient="linear(to-r, #667eea, #764ba2, #f093fb, #667eea)"
              bgSize="200% 100%"
              sx={{
                animation: "gradientShift 3s ease infinite",
                "@keyframes gradientShift": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}
            />

            {/* Header */}
            <Box pt={{ base: 5, md: 8 }} pb={{ base: 3, md: 4 }} px={{ base: 4, md: 8 }}>
              <VStack align="stretch" spacing={{ base: 3, md: 4 }}>
                <Flex align="center" justify="space-between">
                  <HStack spacing={{ base: 2, md: 4 }}>
                    <Box
                      p={{ base: 2, md: 3 }}
                      bg={ACCENT_GRADIENT}
                      borderRadius="xl"
                      boxShadow="0 8px 16px rgba(102, 126, 234, 0.3)"
                    >
                      <Icon
                        as={TimeIcon}
                        color="white"
                        boxSize={{ base: 4, md: 6 }}
                      />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text
                        fontSize={{ base: "lg", md: "2xl" }}
                        fontWeight="bold"
                        bgGradient="linear(to-r, #667eea, #764ba2)"
                        bgClip="text"
                      >
                        Changelog
                      </Text>
                      <Text
                        color="whiteAlpha.600"
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="normal"
                      >
                        {totalEntries} updates across{" "}
                        {Object.keys(changelogData).length} months
                      </Text>
                    </VStack>
                  </HStack>

                  <ModalCloseButton
                    position="relative"
                    top="unset"
                    right="unset"
                    color="whiteAlpha.600"
                    _hover={{
                      color: "white",
                      bg: "whiteAlpha.100",
                      transform: "rotate(90deg)",
                    }}
                    transition="all 0.3s ease"
                    borderRadius="lg"
                    size={{ base: "md", md: "lg" }}
                  />
                </Flex>

                <InputGroup size={{ base: "sm", md: "md" }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="whiteAlpha.500" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search updates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="whiteAlpha.50"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="white"
                    _placeholder={{ color: "whiteAlpha.500" }}
                    _hover={{ borderColor: "whiteAlpha.300" }}
                    _focus={{
                      borderColor: "#667eea",
                      boxShadow: "0 0 0 1px #667eea",
                      bg: "whiteAlpha.100",
                    }}
                    borderRadius="xl"
                  />
                </InputGroup>
              </VStack>
            </Box>

            {/* Body */}
            <Box
              flex={1}
              maxH={{ base: "none", sm: "60vh" }}
              overflowY="auto"
              px={{ base: 3, md: 8 }}
              pb={{ base: 4, md: 6 }}
              sx={{
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": {
                  bg: "whiteAlpha.50",
                  borderRadius: "full",
                },
                "&::-webkit-scrollbar-thumb": {
                  bg: "whiteAlpha.300",
                  borderRadius: "full",
                  _hover: { bg: "whiteAlpha.400" },
                },
              }}
            >
              <Stack spacing={{ base: 2, md: 4 }}>
                {Object.entries(filteredData).map(([month, entries]) => {
                  const isMonthOpen = openMonth === month;
                  return (
                    <Box key={month}>
                      <MonthButton
                        month={month}
                        count={entries.length}
                        isOpen={isMonthOpen}
                        onToggle={() =>
                          setOpenMonth(isMonthOpen ? null : month)
                        }
                      />

                      <Collapse in={isMonthOpen} animateOpacity>
                        <VStack
                          mt={{ base: 2, md: 3 }}
                          spacing={{ base: 2, md: 3 }}
                          align="stretch"
                          pl={{ base: 1, md: 2 }}
                        >
                          {entries.map((entry, idx) => (
                            <ChangelogEntry
                              key={idx}
                              entry={entry}
                              index={idx}
                            />
                          ))}
                        </VStack>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>

              {Object.keys(filteredData).length === 0 && (
                <Box textAlign="center" py={12}>
                  <Text color="whiteAlpha.500" fontSize={{ base: "sm", md: "lg" }}>
                    No updates found matching "{searchTerm}"
                  </Text>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              borderTop="1px solid"
              borderColor="whiteAlpha.200"
              px={{ base: 4, md: 8 }}
              py={{ base: 3, md: 5 }}
              bg="whiteAlpha.50"
            >
              <Button
                bg={ACCENT_GRADIENT}
                color="white"
                onClick={onClose}
                size={{ base: "md", md: "lg" }}
                w={{ base: "full", sm: "auto" }}
                px={8}
                borderRadius="xl"
                fontWeight="600"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.3s ease"
              >
                Close
              </Button>
            </Box>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ModalLog;